<script lang="ts">
  interface ThreadsPost {
    id: string;
    threadsId: string;
    content: string;
    fullContent: string;
    authorUsername: string;
    authorDisplayName: string;
    authorAvatarUrl: string;
    likesCount: number;
    repliesCount: number;
    postedAt: string;
    threadsUrl: string;
    hasMedia: boolean;
    hasThread: boolean;
    threadRepliesCount: number;
    mediaType: string | null;
    mediaUrls: string[];
  }

  interface Props {
    post: ThreadsPost | null;
    isOpen: boolean;
    onClose: () => void;
  }

  let { post, isOpen, onClose }: Props = $props();

  function formatTime(dateString: string) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  function handleBackdropKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClose();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isOpen) {
      onClose();
    }
  }

  $effect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = '';
    };
  });
</script>

{#if isOpen && post}
  <!-- Modal Backdrop -->
  <div
    class="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
    onclick={handleBackdropClick}
    onkeydown={handleBackdropKeydown}
    role="button"
    tabindex="0"
  >
    <!-- Modal Container -->
    <div
      class="relative isolate w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-900 border-2 border-purple-200 dark:border-purple-700 shadow-2xl shadow-purple-500/20"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      tabindex="0"
    >
      <!-- Close Button -->
      <button
        onclick={onClose}
        class="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
        aria-label="닫기"
      >
        <svg class="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>

      <!-- Scrollable Content -->
      <div class="overflow-y-auto max-h-[85vh] bg-white dark:bg-gray-900 p-8">
        <!-- Author Header -->
        <div class="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
          {#if post.authorAvatarUrl}
            <img
              src={post.authorAvatarUrl}
              alt={post.authorUsername}
              class="w-16 h-16 rounded-full ring-4 ring-purple-200 dark:ring-purple-800"
            />
          {:else}
            <div class="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-2xl font-bold ring-4 ring-purple-200 dark:ring-purple-800">
              {post.authorUsername?.[0]?.toUpperCase() || 'C'}
            </div>
          {/if}
          <div class="flex-1">
            <h2 id="modal-title" class="text-xl font-bold text-gray-900 dark:text-white">
              {post.authorDisplayName || post.authorUsername}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              @{post.authorUsername}
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {formatTime(post.postedAt)}
            </p>
          </div>
          {#if post.hasThread}
            <div class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
              스레드 {post.threadRepliesCount}개
            </div>
          {/if}
        </div>

        <!-- Full Content -->
        <div class="prose prose-lg dark:prose-invert max-w-none mb-8">
          <div class="text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
            {post.fullContent}
          </div>
        </div>

        <!-- Media -->
        {#if post.hasMedia && post.mediaUrls?.length > 0}
          <div class="mb-8 rounded-xl overflow-hidden bg-black">
            {#if post.mediaType === 'VIDEO'}
              <video
                src={post.mediaUrls[0]}
                controls
                playsinline
                preload="metadata"
                class="w-full max-h-[500px] object-contain"
                poster={post.mediaUrls.length > 1 ? post.mediaUrls[1] : undefined}
              >
                <track kind="captions" />
              </video>
            {:else if post.mediaType === 'IMAGE' || post.mediaType === 'CAROUSEL_ALBUM'}
              {#each post.mediaUrls as url}
                <img
                  src={url}
                  alt="게시글 이미지"
                  class="w-full object-contain max-h-[500px]"
                  loading="lazy"
                />
              {/each}
            {/if}
          </div>
        {/if}

        <!-- Engagement Stats -->
        <div class="flex items-center gap-6 py-4 border-y border-gray-200 dark:border-gray-700 mb-6">
          <div class="flex items-center gap-2">
            <div class="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
              <svg class="w-5 h-5 text-pink-600 dark:text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-gray-900 dark:text-white">
                {post.likesCount?.toLocaleString() || 0}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">좋아요</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-gray-900 dark:text-white">
                {post.repliesCount?.toLocaleString() || 0}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">댓글</p>
            </div>
          </div>
          {#if post.hasMedia}
            <div class="flex items-center gap-2">
              <div class="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                {#if post.mediaType === 'VIDEO'}
                  <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                {:else}
                  <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                {/if}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {post.mediaType === 'VIDEO' ? '영상' : '이미지'}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">포함됨</p>
              </div>
            </div>
          {/if}
        </div>

        <!-- Source Link -->
        <div class="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-700">
          <div class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                출처
              </h3>
              <p class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                이 게시글은 Threads에서 가져온 원본 컨텐츠입니다
              </p>
              <a
                href={post.threadsUrl}
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors text-sm font-medium"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
                Threads에서 원본 보기
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Custom scrollbar for modal */
  .overflow-y-auto::-webkit-scrollbar {
    width: 8px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background-color: #f3f4f6;
    border-radius: 9999px;
  }

  :global(.dark) .overflow-y-auto::-webkit-scrollbar-track {
    background-color: #1f2937;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: #d8b4fe;
    border-radius: 9999px;
  }

  :global(.dark) .overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: #7e22ce;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: #c084fc;
  }

  :global(.dark) .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: #9333ea;
  }
</style>
